import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlChange } from '../../base/control-change-event';
import { HxBaseControl } from '../../base/hx-base-control';
import { createIdSignal } from '../../base/id-manager';
import { StopPropagationDirective } from '../../directives/stop-propagation.directive';
import { HX_RADIO_GROUP } from './radio-tokens';

/**
 * Individual radio button component. Can be used within an HxRadioGroup component or standalone with FormControl.
 *
 * @example
 *   ```html
 *   <!-- With HxRadioGroup -->
 *   <hx-radio-group [(value)]="selected">
 *     <hx-radio value="option1">Option 1</hx-radio>
 *     <hx-radio value="option2">Option 2</hx-radio>
 *   </hx-radio-group>
 *
 *   <!-- Standalone with FormControl -->
 *   <hx-radio [formControl]="myControl" [value]="true">Accept terms</hx-radio>
 *   ```;
 */
@Component({
  selector: 'hx-radio',
  templateUrl: './radio.html',
  styleUrls: ['./radio.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [StopPropagationDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxRadio),
      multi: true,
    },
  ],
  host: {
    class: 'hx-radio',
    '[class.hx-radio--checked]': 'checked()',
    '[class.hx-radio--disabled]': '_isDisabled()',
    '[class.hx-radio--readonly]': 'readonly()',
    '[class.hx-radio--error]': '_isInErrorState()',
    '[attr.role]': '"radio"',
    '[attr.aria-checked]': 'checked()',
    '[attr.aria-disabled]': '_isDisabled() || null',
    '[attr.aria-readonly]': 'readonly() || null',
    '[attr.aria-posinset]': '_ariaPosinset()',
    '[attr.aria-setsize]': '_ariaSetsize()',
    '[attr.tabindex]': 'tabindex()',
  },
})
export class HxRadio<T = any> extends HxBaseControl implements AfterViewInit, OnDestroy {
  /**
   * Value of this radio option. This value is used to identify which radio is selected in the group.
   *
   * @example
   *   ```html
   *   <hx-radio value="option1">Option 1</hx-radio>
   *   ```;
   *
   * @required
   */
  readonly value = input.required<T>();

  /**
   * Optional externally-controlled checked state, for standalone usage outside a radio group/form (e.g. inside a data
   * table). When provided, this takes precedence over the internal standalone checked state.
   *
   * @default undefined
   */
  readonly checkedOverride = input<boolean | undefined>(undefined);

  /**
   * Whether this radio button is currently checked. Automatically computed by comparing this radio's value with the
   * parent group's value or standalone checked state.
   *
   * @computed
   */
  readonly checked = computed(() => {
    const override = this.checkedOverride();
    if (override !== undefined) {
      return override;
    }
    if (!this._radioGroup) {
      // Standalone mode: use internal checked state
      return this._standaloneChecked();
    }
    return this._radioGroup.value() === this.value();
  });

  /**
   * Whether this radio button is disabled. Combines individual disabled state with parent group's disabled state.
   * Disabled radios cannot be selected and are skipped during keyboard navigation. Uses base class disabledState() and
   * combines with radio group's disabled state.
   */
  readonly _isDisabled = computed(() => {
    return this.disabledState() || (this._radioGroup?.disabledState() ?? false);
  });

  /**
   * Name attribute for the radio button. Typically inherited from the parent HxRadioGroup.
   *
   * @default ''
   */
  readonly name = input<string>('');

  /**
   * Emits when this radio button is selected via user interaction. The event includes the source radio and its value.
   *
   * @example
   *   ```html
   *   <hx-radio value="yes" (change)="onRadioChange($event)">Yes</hx-radio>
   *   ```;
   */
  readonly change = output<ControlChange<T>>();

  /** Optional input ID (will auto-generate if not provided) */
  readonly idInput = input<string | undefined>(undefined, { alias: 'id' });

  /** Unique computed ID for this radio */
  readonly computedId = createIdSignal(this.idInput, 'hx-radio-');

  /** Parent radio group (injected) */
  protected readonly _radioGroup = inject(HX_RADIO_GROUP, { optional: true });

  /** Tabindex for sequential Tab navigation. Defaults to 0 when enabled, -1 when disabled. */
  readonly tabindex = signal<number>(0);

  /** Element reference for focus management */
  readonly _elementRef = inject(ElementRef);

  /** Native input reference for triggering native click / change events on keyboard activation */
  readonly _nativeInput = viewChild<ElementRef<HTMLInputElement>>('nativeInput');

  /** Focus monitor for keyboard/mouse focus detection */
  private readonly _focusMonitor = inject(FocusMonitor);

  /** Internal checked state for standalone usage (without radio group) */
  private readonly _standaloneChecked = signal<boolean>(false);

  /** ControlValueAccessor callbacks for standalone mode */
  protected _onChange: (value: T | boolean) => void = () => {};
  protected _onTouched: () => void = () => {};

  /** Whether this radio is in error state (inherited from group) */
  protected readonly _isInErrorState = computed(() => {
    return this._radioGroup?._isInErrorState() ?? false;
  });

  /** Total number of radios in group (for aria-setsize) */
  protected readonly _ariaSetsize = computed(() => {
    return this._radioGroup?._radios().length ?? 1;
  });

  /** Position of this radio in group (for aria-posinset) */
  protected readonly _ariaPosinset = computed(() => {
    if (!this._radioGroup) {
      return 1;
    }
    const radios = this._radioGroup._radios();
    return radios.indexOf(this) + 1;
  });

  constructor() {
    super();
    effect(() => {
      this.tabindex.set(this._isDisabled() ? -1 : 0);
    });
  }

  /**
   * Handles keyboard navigation directly on the radio host for standalone usage.
   *
   * @param event The keydown event on the radio host
   * @internal
   */
  @HostListener('keydown', ['$event'])
  protected _onHostKeydown(event: KeyboardEvent): void {
    if (this._isDisabled() || this.readonly()) {
      return;
    }
    if (!this._radioGroup && (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter')) {
      event.preventDefault();
      const input = this._nativeInput()?.nativeElement;
      if (input) {
        input.click();
      } else {
        const nextChecked = !this.checked();
        this._standaloneChecked.set(nextChecked);
        const nextValue = nextChecked ? this.value() : (null as any);
        this._onChange(nextValue);
        this.change.emit(new ControlChange(this, nextValue));
      }
    }
  }

  /**
   * Prevents the browser's native arrow key handling from auto-selecting the sibling native radio button.
   *
   * @param event The keydown event from the native radio input
   * @internal
   */
  protected _onNativeKeydown(event: KeyboardEvent): void {
    if (event.key.startsWith('Arrow')) {
      event.preventDefault();
    }
  }

  /**
   * Handles native input change events.
   *
   * @param event The change event from the native radio input
   * @internal
   */
  protected _onInputChange(event: Event): void {
    if (this._isDisabled() || this.readonly()) {
      return;
    }
    if (this._radioGroup) {
      this._radioGroup._updateValue(this.value());
      this.change.emit(new ControlChange(this, this.value()));
    } else {
      // Standalone mode: update internal state and notify FormControl
      this._standaloneChecked.set(true);
      this._onChange(this.value());
      this.change.emit(new ControlChange(this, this.value()));
    }
  }

  /**
   * Handles native input click events to allow radio deselection.
   *
   * @param event The click event from the native radio input
   * @internal
   */
  protected _onInputClick(event: MouseEvent): void {
    if (this._isDisabled() || this.readonly()) {
      return;
    }
    // If it's already checked, clicking it again should deselect it
    if (this.checked()) {
      event.preventDefault(); // Prevent native check behavior from forcing it to stay checked
      if (this._radioGroup) {
        this._radioGroup._updateValue(null);
      } else {
        this._standaloneChecked.set(false);
        this._onChange(null as any);
        this.change.emit(new ControlChange(this, null as any));
      }
    }
  }

  /**
   * Programmatically focus this radio button. Used internally for keyboard navigation and can be called by consumers.
   *
   * @example
   *   ```typescript
   *   \@ViewChild(HxRadio) radio!: HxRadio;
   *   this.radio.focus();
   *   ```;
   *
   * @public
   */
  focus(): void {
    this._elementRef.nativeElement.focus();
  }

  /** Initialize focus monitoring */
  ngAfterViewInit(): void {
    this._focusMonitor.monitor(this._elementRef, true);
  }

  /** Cleanup focus monitoring */
  ngOnDestroy(): void {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  // ControlValueAccessor implementation (for standalone usage without HxRadioGroup)

  /**
   * Write value from FormControl to the radio button.
   *
   * @param value The value from the FormControl
   */
  writeValue(value: T | boolean): void {
    if (!this._radioGroup) {
      // Standalone mode: compare value with radio's value or treat as boolean
      if (typeof value === 'boolean') {
        this._standaloneChecked.set(value);
      } else {
        this._standaloneChecked.set(value === this.value());
      }
    }
  }

  /**
   * Register callback to be called when the radio value changes.
   *
   * @param fn Callback function
   */
  registerOnChange(fn: (value: T | boolean) => void): void {
    this._onChange = fn;
  }

  /**
   * Register callback to be called when the radio is touched/blurred.
   *
   * @param fn Callback function
   */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /**
   * Set the disabled state from FormControl.
   *
   * @param isDisabled Whether the control should be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    // Disabled state is already handled via the disabled input
    // This method is required by ControlValueAccessor but the disabled
    // state is managed through the component's disabled input
  }
}
